import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ConstructionIcon from '@mui/icons-material/Construction';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentsIcon from '@mui/icons-material/Payments';
import HandymanIcon from '@mui/icons-material/Handyman';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ComposedChart,
  Area,
  ScatterChart,
  Scatter,
  ZAxis,
} from 'recharts';
import ProcurementContainer from '../../procurement/containers/ProcurementContainer';
import {
  BomItemWithRelations,
  Development,
  ItemTransferWithRelations,
  PurchaseOrderWithRelations,
  ProcurementProcessWithRelations,
  useGetBomItemsByProjectByProjectIdQuery,
  useGetBomQuery,
  useGetDevelopmentsQuery,
  useGetItemsIssuedQuery,
  useGetItemTransfersQuery,
  useGetPurchaseOrdersQuery,
  useGetProcurementProcessesQuery,
} from '../../../redux/vmsApi';
import { getCurrentDate } from '../../../utils/dateUtil';
import { formatCurrency } from '../../../utils/currencyUtil';

type PeriodMode = 'day' | 'week' | 'month' | 'quarter' | 'year';

type TimeRow = {
  bucket: string;
  orderedValue: number;
  deliveredValue: number;
  paidValue: number;
  issuedValue: number;
  remainingBudgetValue: number;
  orderedQty: number;
  deliveredQty: number;
  paidQty: number;
  issuedQty: number;
  budgetQty: number;
};

type MetricCardProps = {
  title: string;
  valueLabel: string;
  quantityLabel: string;
  value: number;
  quantity: number;
  showQuantity?: boolean;
  accent: string;
  icon: React.ReactNode;
  subtitle?: string;
  quantityUnit?: string;
  currencyCode?: string;
};

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  valueLabel,
  quantityLabel,
  value,
  quantity,
  showQuantity = true,
  accent,
  icon,
  subtitle,
  quantityUnit,
  currencyCode = 'USD',
}) => (
  <Card
    sx={{
      borderRadius: 3,
      height: '100%',
      background: `linear-gradient(145deg, ${accent}22 0%, #ffffff 68%)`,
      border: `1px solid ${accent}55`,
      boxShadow: `0 10px 24px ${accent}22`,
      transition: 'transform .2s ease, box-shadow .2s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: `0 14px 32px ${accent}33`,
      },
    }}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="subtitle2" sx={{ color: '#2f3e46', fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: '#5b6770', display: 'block', mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color: accent }}>{icon}</Box>
      </Stack>

      <Typography variant="h5" sx={{ fontWeight: 800, color: accent, mt: 1.5 }}>
        {formatCurrency(value, currencyCode)}
      </Typography>
      <Typography variant="body2" sx={{ color: '#415a77' }}>
        {valueLabel}
      </Typography>

      {showQuantity ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1b263b', mt: 1.5 }}>
            {quantity.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </Typography>
          <Typography variant="body2" sx={{ color: '#415a77' }}>
            {quantityUnit ? `${quantityLabel} (${quantityUnit})` : quantityLabel}
          </Typography>
        </>
      ) : (
        <Typography variant="body2" sx={{ color: '#64748b', mt: 1.5 }}>
          Quantity shown only when a single BOM item is selected.
        </Typography>
      )}
    </CardContent>
  </Card>
);

const toDate = (value?: unknown): Date | null => {
  if (!value) {
    return null;
  }
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const inRange = (date: Date | null, fromDate: string, toDateValue: string): boolean => {
  if (!date) {
    return false;
  }
  const from = new Date(`${fromDate}T00:00:00`);
  const to = new Date(`${toDateValue}T23:59:59.999`);
  return date >= from && date <= to;
};

const normalizeText = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const getTimeBucket = (date: Date, mode: PeriodMode): string => {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();

  if (mode === 'day') {
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  if (mode === 'week') {
    const jan1 = new Date(y, 0, 1);
    const dayOffset = Math.floor((date.getTime() - jan1.getTime()) / 86400000);
    const week = Math.floor((dayOffset + jan1.getDay()) / 7) + 1;
    return `${y}-W${String(week).padStart(2, '0')}`;
  }

  if (mode === 'month') {
    return `${y}-${String(m).padStart(2, '0')}`;
  }

  if (mode === 'quarter') {
    const quarter = Math.floor((m - 1) / 3) + 1;
    return `${y}-Q${quarter}`;
  }

  return `${y}`;
};

const CostToCompletionPage: React.FC = () => {
  const [fromDate, setFromDate] = useState('2025-01-01');
  const [toDateValue, setToDateValue] = useState(getCurrentDate());
  const [selectedDevelopmentId, setSelectedDevelopmentId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedBomItemId, setSelectedBomItemId] = useState('all');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('month');

  const { data: developments = [], isLoading: developmentsLoading } = useGetDevelopmentsQuery();
  const { data: boms = [] } = useGetBomQuery({});
  const { data: procurementProcesses = [] } = useGetProcurementProcessesQuery({ limit: 5000, skip: 0 });
  const { data: itemTransfers = [] } = useGetItemTransfersQuery({ limit: 5000, skip: 0 });

  const selectedDevelopment = useMemo(
    () => developments.find((d) => String(d.id) === selectedDevelopmentId),
    [developments, selectedDevelopmentId],
  );

  const availableProjects = selectedDevelopment?.projects ?? [];

  const { data: projectBomItems = [] } = useGetBomItemsByProjectByProjectIdQuery(
    { projectId: selectedProjectId, limit: 5000, skip: 0 },
    { skip: !selectedProjectId },
  );

  const selectedBomItem = useMemo(
    () => projectBomItems.find((item) => String(item.id) === selectedBomItemId),
    [projectBomItems, selectedBomItemId],
  );

  const bomItemOptions = useMemo(
    () => [
      { id: 'all', label: 'All BOM Items' },
      ...projectBomItems.map((item) => ({
        id: String(item.id),
        label: `${item.itemCode} - ${item.itemDescription}`,
      })),
    ],
    [projectBomItems],
  );

  const projectBom = useMemo(
    () => boms.find((bom) => String(bom.project?.id) === selectedProjectId),
    [boms, selectedProjectId],
  );

  const selectedProjectName =
    availableProjects.find((project) => String(project.id) === selectedProjectId)?.name ?? '';

  const selectedDevelopmentName = selectedDevelopment?.name ?? '';

  const { data: purchaseOrders = [] } = useGetPurchaseOrdersQuery(
    {
      development: selectedDevelopmentName,
      project: selectedProjectName,
      fromDate,
      toDate: toDateValue,
      limit: 5000,
      skip: 0,
      detailed: true,
    },
    { skip: !selectedDevelopmentId || !selectedProjectId },
  );

  const { data: issuedItems = [] } = useGetItemsIssuedQuery(
    {
      projectAllocation: selectedProjectName,
      limit: 5000,
      skip: 0,
    },
    { skip: !selectedDevelopmentId || !selectedProjectId },
  );

  const scopedBomItems = useMemo(() => {
    if (!selectedProjectId) {
      return [] as BomItemWithRelations[];
    }
    if (selectedBomItemId === 'all') {
      return projectBomItems;
    }
    return projectBomItems.filter((item) => String(item.id) === selectedBomItemId);
  }, [projectBomItems, selectedProjectId, selectedBomItemId]);

  const isSingleBomItemSelected = selectedBomItemId !== 'all' && scopedBomItems.length === 1;
  const bomItemUnit = selectedBomItem?.itemUnit ?? '';

  const filteredPurchaseOrders = useMemo(() => {
    if (!selectedCurrency || selectedCurrency === 'All') {
      return purchaseOrders;
    }

    const expectedCurrency = selectedCurrency.toUpperCase();
    return purchaseOrders.filter((purchaseOrder) => normalizeText(purchaseOrder.quotation?.currency) === expectedCurrency.toLowerCase());
  }, [purchaseOrders, selectedCurrency]);

  const scopedItemCodes = useMemo(
    () => new Set(scopedBomItems.map((item) => normalizeText(item.itemCode)).filter(Boolean)),
    [scopedBomItems],
  );

  const procurementByProject = useMemo(() => {
    if (!selectedDevelopment || !selectedProjectId) {
      return [] as ProcurementProcessWithRelations[];
    }

    const selectedProjectName = normalizeText(
      availableProjects.find((project) => String(project.id) === selectedProjectId)?.name,
    );
    const selectedDevelopmentName = normalizeText(selectedDevelopment.name);

    return procurementProcesses.filter((process) => {
      const requisition = process.requisition;
      const devName = normalizeText(requisition?.development?.name);
      const projName = normalizeText(requisition?.project?.name);

      if (!devName || !projName) {
        return false;
      }

      const devMatch = devName === selectedDevelopmentName;
      const projectMatch = projName === selectedProjectName;
      if (!devMatch || !projectMatch) {
        return false;
      }

      const date = toDate(process.lastActionedDate || process.initiatedDate);
      return inRange(date, fromDate, toDateValue);
    });
  }, [procurementProcesses, selectedDevelopment, selectedProjectId, availableProjects, fromDate, toDateValue]);

  const issuedTransfers = useMemo(
    () => {
      if (!selectedProjectId) {
        return [] as ItemTransferWithRelations[];
      }

      const selectedProjectNameNormalized = normalizeText(selectedProjectName);

      return itemTransfers.filter((transfer) => {
        const sourceProjectName = normalizeText(transfer.sourceLocation?.project?.name);
        const destinationProjectName = normalizeText(transfer.destinationLocation?.project?.name);
        const hasProject =
          sourceProjectName === selectedProjectNameNormalized ||
          destinationProjectName === selectedProjectNameNormalized;

        if (!hasProject) {
          return false;
        }

        const date = toDate(transfer.transferredDate);
        return inRange(date, fromDate, toDateValue) && transfer.transferType === 'Goods Issued';
      });
    },
    [itemTransfers, selectedProjectId, selectedProjectName, fromDate, toDateValue],
  );

  const transferItemPassesBomFilter = (itemCode: string): boolean => {
    const code = normalizeText(itemCode);
    if (!scopedItemCodes.size) {
      return true;
    }
    return scopedItemCodes.has(code);
  };

  const deliveredSummary = useMemo(() => {
    const summarize = (rows: PurchaseOrderWithRelations[]) => {
      let qty = 0;
      let value = 0;
      let unit = '';

      rows.forEach((purchaseOrder) => {
        const date = toDate(purchaseOrder.date);
        if (!inRange(date, fromDate, toDateValue)) {
          return;
        }

        if (!isSingleBomItemSelected) {
          value += Number(purchaseOrder.fullFilledValue ?? 0) || 0;
          return;
        }

        (purchaseOrder.itemsReceived ?? []).forEach((item) => {
          if (!transferItemPassesBomFilter(item.itemCode)) {
            return;
          }

          qty += Number(item.quantity?.quantityValue ?? 0) || 0;
          value += Number(item.totalCostPerItem ?? 0) || 0;
          if (!unit && item.quantity?.quantityUnit) {
            unit = item.quantity.quantityUnit;
          }
        });
      });

      return {
        qty: isSingleBomItemSelected ? qty : 0,
        value,
        unit: isSingleBomItemSelected ? unit : '',
      };
    };

    return summarize(filteredPurchaseOrders);
  }, [filteredPurchaseOrders, fromDate, toDateValue, isSingleBomItemSelected, scopedItemCodes]);

  const budgetValue = useMemo(
    () => scopedBomItems.reduce((sum, item) => sum + Number(item.itemCurrentValue ?? 0), 0),
    [scopedBomItems],
  );

  const budgetInitialValue = useMemo(
    () => scopedBomItems.reduce((sum, item) => sum + Number(item.itemInitialValue ?? 0), 0),
    [scopedBomItems],
  );

  const budgetQty = useMemo(
    () => (isSingleBomItemSelected
      ? scopedBomItems.reduce((sum, item) => sum + Number(item.itemQuantity ?? 0), 0)
      : 0),
    [scopedBomItems, isSingleBomItemSelected],
  );

  const budgetInitialQty = useMemo(
    () => (isSingleBomItemSelected
      ? scopedBomItems.reduce((sum, item) => sum + Number(item.initialQuantity ?? 0), 0)
      : 0),
    [scopedBomItems, isSingleBomItemSelected],
  );

  const orderedSummary = useMemo(() => {
    let value = 0;
    let qty = 0;
    let unit = '';

    filteredPurchaseOrders.forEach((purchaseOrder) => {
      const date = toDate(purchaseOrder.date);
      if (!inRange(date, fromDate, toDateValue)) {
        return;
      }

      if (!isSingleBomItemSelected) {
        value += Number(purchaseOrder.quotation?.grandTotal ?? 0) || 0;
        return;
      }

      (purchaseOrder.quotation?.quotationItems ?? []).forEach((item) => {
        if (!transferItemPassesBomFilter(item.itemCode)) {
          return;
        }

        qty += Number(item.quantity?.quantityValue ?? 0) || 0;
        value += Number(item.totalCostPerItem ?? 0) || 0;
        if (!unit && item.quantity?.quantityUnit) {
          unit = item.quantity.quantityUnit;
        }
      });
    });

    return {
      value,
      qty: isSingleBomItemSelected ? qty : 0,
      unit: isSingleBomItemSelected ? unit : '',
    };
  }, [purchaseOrders, fromDate, toDateValue, isSingleBomItemSelected, scopedItemCodes]);

  const orderedValue = orderedSummary.value;
  const orderedQty = orderedSummary.qty;
  const orderedUnit = orderedSummary.unit || bomItemUnit;

  const orderedViaBudgetValue = useMemo(
    () => scopedBomItems.reduce((sum, item) => sum + Number(item.procuredValue ?? 0), 0),
    [scopedBomItems],
  );

  const orderedViaBudgetQty = useMemo(
    () => (isSingleBomItemSelected
      ? scopedBomItems.reduce((sum, item) => sum + Number(item.procuredQuantity ?? 0), 0)
      : 0),
    [scopedBomItems, isSingleBomItemSelected],
  );

  const deliveredValue = deliveredSummary.value;
  const deliveredQty = deliveredSummary.qty;
  const deliveredUnit = deliveredSummary.unit || bomItemUnit;

  const issuedValue = useMemo(() => {
    let total = 0;

    issuedItems.forEach((item) => {
      if (!transferItemPassesBomFilter(item.itemCode)) {
        return;
      }

      total += Number(item.totalValue ?? 0) || 0;
    });

    return total;
  }, [issuedItems, scopedItemCodes]);

  const issuedQty = useMemo(() => {
    if (!isSingleBomItemSelected) {
      return 0;
    }

    let total = 0;
    issuedItems.forEach((item) => {
      if (!transferItemPassesBomFilter(item.itemCode)) {
        return;
      }

      total += Number(item.quantity ?? 0) || 0;
    });

    return total;
  }, [issuedItems, isSingleBomItemSelected, scopedItemCodes]);

  const paidSummary = useMemo(() => {
    let value = 0;
    let qty = 0;

    filteredPurchaseOrders.forEach((purchaseOrder) => {
      if (!isSingleBomItemSelected) {
        value += Number(purchaseOrder.paid ?? 0) || 0;
        return;
      }

      const status = normalizeText(purchaseOrder.paymentStatus);
      if (status !== 'fully paid' && status !== 'partially paid') {
        return;
      }

      const quotationItems = purchaseOrder.quotation?.quotationItems ?? [];
      if (status === 'fully paid') {
        quotationItems.forEach((item) => {
          if (!transferItemPassesBomFilter(item.itemCode)) {
            return;
          }

          value += Number(item.totalCostPerItem ?? 0) || 0;
          qty += Number(item.quantity?.quantityValue ?? 0) || 0;
        });
        return;
      }

      const grandTotal = Number(purchaseOrder.quotation?.grandTotal ?? 0) || 0;
      if (!grandTotal) {
        return;
      }

      const paidAmount = Number(purchaseOrder.paid ?? 0) || 0;
      const ratio = Math.min(Math.max(paidAmount / grandTotal, 0), 1);

      quotationItems.forEach((item) => {
        if (!transferItemPassesBomFilter(item.itemCode)) {
          return;
        }

        value += (Number(item.totalCostPerItem ?? 0) || 0) * ratio;
        qty += (Number(item.quantity?.quantityValue ?? 0) || 0) * ratio;
      });
    });

    return {
      value,
      qty: isSingleBomItemSelected ? qty : 0,
    };
  }, [filteredPurchaseOrders, isSingleBomItemSelected, scopedItemCodes]);

  const paidValue = paidSummary.value;
  const paidQty = paidSummary.qty;

  const remainingBudgetValue = Math.max(budgetValue - paidValue, 0);
  const remainingBudgetQty = Math.max(budgetQty - issuedQty, 0);

  const undeliveredValue = Math.max(orderedValue - deliveredValue, 0);
  const undeliveredQty = Math.max(orderedQty - deliveredQty, 0);

  const usageBudgetValue = budgetValue;
  const usageBudgetQty = budgetQty;

  const stockValue = Math.max(deliveredValue - issuedValue, 0);
  const stockQty = Math.max(deliveredQty - issuedQty, 0);
  const stockUnit = deliveredUnit || bomItemUnit;

  const analyticsRows = useMemo(() => {
    const rowsByBucket = new Map<string, TimeRow>();

    const ensureRow = (bucket: string): TimeRow => {
      const existing = rowsByBucket.get(bucket);
      if (existing) {
        return existing;
      }
      const created: TimeRow = {
        bucket,
        orderedValue: 0,
        deliveredValue: 0,
        paidValue: 0,
        issuedValue: 0,
        remainingBudgetValue: 0,
        orderedQty: 0,
        deliveredQty: 0,
        paidQty: 0,
        issuedQty: 0,
        budgetQty: 0,
      };
      rowsByBucket.set(bucket, created);
      return created;
    };

    const useCodeFilter = (itemCode?: string): boolean => {
      if (!itemCode || !scopedItemCodes.size) {
        return true;
      }
      return scopedItemCodes.has(normalizeText(itemCode));
    };

    procurementByProject.forEach((process) => {
      const date = toDate(process.lastActionedDate || process.initiatedDate);
      if (!date) {
        return;
      }

      const bucket = getTimeBucket(date!, periodMode);
      const row = ensureRow(bucket);

      (process.requisition?.items ?? []).forEach((item) => {
        if (!useCodeFilter(item.itemCode)) {
          return;
        }
        if (isSingleBomItemSelected) {
          row.orderedQty += Number(item.quantity?.quantityValue ?? 0) || 0;
        }
        row.orderedValue += Number(item.totalCostPerItem ?? 0) || 0;
      });
    });

    purchaseOrders.forEach((purchaseOrder) => {
      const date = toDate(purchaseOrder.date);
      if (!date || !inRange(date, fromDate, toDateValue)) {
        return;
      }
      const bucket = getTimeBucket(date!, periodMode);
      const row = ensureRow(bucket);

      if (!isSingleBomItemSelected) {
        row.deliveredValue += Number(purchaseOrder.fullFilledValue ?? 0) || 0;
        return;
      }

      (purchaseOrder.itemsReceived ?? []).forEach((item) => {
        if (!useCodeFilter(item.itemCode)) {
          return;
        }
        row.deliveredQty += Number(item.quantity?.quantityValue ?? 0) || 0;
        row.deliveredValue += Number(item.totalCostPerItem ?? 0) || 0;
      });
    });

    issuedTransfers.forEach((transfer) => {
      const date = toDate(transfer.transferredDate);
      if (!date) {
        return;
      }
      const bucket = getTimeBucket(date!, periodMode);
      const row = ensureRow(bucket);

      (transfer.items ?? []).forEach((item) => {
        if (!useCodeFilter(item.itemCode)) {
          return;
        }
        if (isSingleBomItemSelected) {
          row.issuedQty += Number(item.quantity?.quantityValue ?? 0) || 0;
        }
        row.issuedValue += Number(item.totalCostPerItem ?? 0) || 0;
      });
    });

    purchaseOrders.forEach((purchaseOrder) => {
      const date = toDate(purchaseOrder.date);
      if (!date || !inRange(date, fromDate, toDateValue)) {
        return;
      }

      const bucket = getTimeBucket(date!, periodMode);
      const row = ensureRow(bucket);

      if (!isSingleBomItemSelected) {
        row.paidValue += Number(purchaseOrder.paid ?? 0) || 0;
        return;
      }

      const status = normalizeText(purchaseOrder.paymentStatus);
      if (status !== 'fully paid' && status !== 'partially paid') {
        return;
      }

      const quotationItems = purchaseOrder.quotation?.quotationItems ?? [];
      if (status === 'fully paid') {
        quotationItems.forEach((item) => {
          if (!useCodeFilter(item.itemCode)) {
            return;
          }
          row.paidValue += Number(item.totalCostPerItem ?? 0) || 0;
          row.paidQty += Number(item.quantity?.quantityValue ?? 0) || 0;
        });
        return;
      }

      const grandTotal = Number(purchaseOrder.quotation?.grandTotal ?? 0) || 0;
      if (!grandTotal) {
        return;
      }

      const paidAmount = Number(purchaseOrder.paid ?? 0) || 0;
      const ratio = Math.min(Math.max(paidAmount / grandTotal, 0), 1);

      quotationItems.forEach((item) => {
        if (!useCodeFilter(item.itemCode)) {
          return;
        }
        row.paidValue += (Number(item.totalCostPerItem ?? 0) || 0) * ratio;
        row.paidQty += (Number(item.quantity?.quantityValue ?? 0) || 0) * ratio;
      });
    });

    const rows = Array.from(rowsByBucket.values()).sort((a, b) =>
      a.bucket.localeCompare(b.bucket),
    );

    let runningOrdered = 0;
    let runningPaid = 0;
    let runningIssuedQty = 0;

    rows.forEach((row) => {
      runningOrdered += row.orderedValue;
      runningPaid += row.paidValue;
      runningIssuedQty += row.issuedQty;
      row.budgetQty = budgetQty;
      row.remainingBudgetValue = Math.max(budgetValue - runningPaid, 0);
      row.budgetQty = isSingleBomItemSelected ? Math.max(budgetQty - runningIssuedQty, 0) : 0;

      if (!isSingleBomItemSelected) {
        row.orderedQty = 0;
        row.deliveredQty = 0;
        row.paidQty = 0;
        row.issuedQty = 0;
      }
    });

    return rows;
  }, [
    procurementByProject,
    filteredPurchaseOrders,
    issuedTransfers,
    periodMode,
    scopedItemCodes,
    fromDate,
    toDateValue,
    budgetValue,
    budgetQty,
    isSingleBomItemSelected,
  ]);

  const statusReady = Boolean(selectedDevelopmentId && selectedProjectId);

  const selectedProjectLabel = selectedProjectName || 'All Project Items';

  const handlePeriodMode = (_event: React.MouseEvent<HTMLElement>, next: PeriodMode | null) => {
    if (next) {
      setPeriodMode(next);
    }
  };

  const handleDevelopmentChange = (event: SelectChangeEvent<string>) => {
    setSelectedDevelopmentId(event.target.value);
    setSelectedProjectId('');
    setSelectedBomItemId('all');
  };

  const handleProjectChange = (event: SelectChangeEvent<string>) => {
    setSelectedProjectId(event.target.value);
    setSelectedBomItemId('all');
  };

  return (
    <ProcurementContainer>
      <Stack spacing={3}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 4,
            background:
              'radial-gradient(circle at 10% 20%, #e0f7fa 0%, #fce4ec 35%, #f3e5f5 55%, #e8f5e9 100%)',
            border: '1px solid #d0d7de',
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.14)',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Typography variant="h1" gutterBottom sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
                Cost To Completion
              </Typography>
              <Typography variant="body1" sx={{ color: '#334155' }}>
                Executive analytics for budget usage, procurement, delivery, payments, issuance, and remaining BOM runway.
              </Typography>
            </Box>
            <Box sx={{ color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoGraphIcon />
              <Typography sx={{ fontWeight: 700 }}>Executive View</Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 3, backgroundColor: '#f8fafc', boxShadow: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2.4}>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={2.4}>
              <TextField
                label="To Date"
                type="date"
                value={toDateValue}
                onChange={(event) => setToDateValue(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={2.4}>
              <FormControl fullWidth disabled={developmentsLoading}>
                <InputLabel>Development</InputLabel>
                <Select value={selectedDevelopmentId} label="Development" onChange={handleDevelopmentChange}>
                  {developments.map((development: Development) => (
                    <MenuItem key={String(development.id)} value={String(development.id)}>
                      {development.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <FormControl fullWidth disabled={!selectedDevelopmentId}>
                <InputLabel>Project</InputLabel>
                <Select value={selectedProjectId} label="Project" onChange={handleProjectChange}>
                  {availableProjects.map((project) => (
                    <MenuItem key={String(project.id)} value={String(project.id)}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2.4}>
              <Autocomplete
                options={bomItemOptions}
                value={bomItemOptions.find((opt) => opt.id === selectedBomItemId) ?? bomItemOptions[0] ?? null}
                onChange={(_event, option) => setSelectedBomItemId(option?.id ?? 'all')}
                disabled={!selectedProjectId}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label="BOM Item" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12} md={2.4}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select value={selectedCurrency} label="Currency" onChange={(event) => setSelectedCurrency(event.target.value)}>
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="ZAR">ZAR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="AUD">AUD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {!statusReady && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Select Development and Project to load Cost To Completion analytics.
            </Alert>
          )}

          {statusReady && (
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} mt={2} alignItems={{ xs: 'flex-start', lg: 'center' }}>
              <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>
                Scope: {selectedProjectLabel}
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>
                BOM: {projectBom?.title ?? '-'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155' }}>
                Selected Item: {selectedBomItem ? `${selectedBomItem.itemCode} - ${selectedBomItem.itemDescription}` : 'All'}
              </Typography>
            </Stack>
          )}
        </Paper>

        {statusReady && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  title="Budget"
                  value={budgetInitialValue}
                  quantity={budgetInitialQty}
                  showQuantity={isSingleBomItemSelected}
                  valueLabel="Initial baseline value"
                  quantityLabel="Initial baseline quantity"
                  quantityUnit={bomItemUnit}
                  accent="#0f766e"
                  icon={<AccountBalanceWalletIcon />}
                  subtitle="Before adjustments"
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  title="Ordered Via Budget"
                  value={orderedViaBudgetValue}
                  quantity={orderedViaBudgetQty}
                  showQuantity={isSingleBomItemSelected}
                  valueLabel="Budget-ordered value"
                  quantityLabel="Budget-ordered quantity"
                  quantityUnit={bomItemUnit}
                  accent="#6366f1"
                  icon={<AccountBalanceWalletIcon />}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  title="BOM Items left"
                  value={usageBudgetValue}
                  quantity={usageBudgetQty}
                  showQuantity={isSingleBomItemSelected}
                  valueLabel="Current budget envelope"
                  quantityLabel="Current budget quantity"
                  quantityUnit={bomItemUnit}
                  accent="#0284c7"
                  icon={<ConstructionIcon />}
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  title="Ordered / Procured"
                  value={orderedValue}
                  quantity={orderedQty}
                  showQuantity={isSingleBomItemSelected}
                  valueLabel="Ordered value"
                  quantityLabel="Ordered quantity"
                  quantityUnit={orderedUnit}
                  accent="#7c3aed"
                  icon={<ShoppingCartIcon />}
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  title="Delivered Materials"
                  value={deliveredValue}
                  quantity={deliveredQty}
                  showQuantity={isSingleBomItemSelected}
                  valueLabel="Delivered value"
                  quantityLabel="Delivered quantity"
                  quantityUnit={deliveredUnit}
                  accent="#0d9488"
                  icon={<LocalShippingIcon />}
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  title="Paid Materials"
                  value={paidValue}
                  quantity={paidQty}
                  showQuantity={isSingleBomItemSelected}
                  valueLabel="Paid value"
                  quantityLabel="Estimated paid quantity"
                  quantityUnit={bomItemUnit}
                  accent="#2563eb"
                  icon={<PaymentsIcon />}
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  title="Issued Materials"
                  value={issuedValue}
                  quantity={issuedQty}
                  showQuantity={isSingleBomItemSelected}
                  valueLabel="Issued value"
                  quantityLabel="Issued quantity"
                  quantityUnit={bomItemUnit}
                  accent="#ea580c"
                  icon={<HandymanIcon />}
                  subtitle="From inventory to project/unit"
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  title="Stock Value"
                  value={stockValue}
                  quantity={stockQty}
                  showQuantity={isSingleBomItemSelected}
                  valueLabel="Delivered less issued value"
                  quantityLabel="Stock quantity"
                  quantityUnit={stockUnit}
                  accent="#dc2626"
                  icon={<Inventory2Icon />}
                  subtitle="Delivered goods minus issued goods"
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <MetricCard
                  title="Remaining Budget"
                  value={remainingBudgetValue}
                  quantity={remainingBudgetQty}
                  showQuantity={isSingleBomItemSelected}
                  valueLabel="Budget value not yet paid"
                  quantityLabel="Budget quantity not yet issued"
                  quantityUnit={bomItemUnit}
                  accent="#1d4ed8"
                  icon={<TrendingUpIcon />}
                  currencyCode={selectedCurrency === 'All' ? 'USD' : selectedCurrency}
                />
              </Grid>
            </Grid>

            <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 3 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Rates Over Time
                </Typography>
                <ToggleButtonGroup
                  value={periodMode}
                  exclusive
                  onChange={handlePeriodMode}
                  color="primary"
                  size="small"
                >
                  <ToggleButton value="day">Day</ToggleButton>
                  <ToggleButton value="week">Week</ToggleButton>
                  <ToggleButton value="month">Month</ToggleButton>
                  <ToggleButton value="quarter">Quarter</ToggleButton>
                  <ToggleButton value="year">Year</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
                Delivery, payment, ordering, issuance and budget balance rates grouped by {periodMode}.
              </Typography>

              <Box sx={{ width: '100%', height: 360 }}>
                <ResponsiveContainer>
                  <LineChart data={analyticsRows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" />
                    <YAxis
                      tickMargin={8}
                      width={90}
                      tickFormatter={(value) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)}
                    />
                    <Tooltip formatter={(value: number) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)} />
                    <Legend />
                    <Line type="monotone" dataKey="orderedValue" stroke="#7c3aed" strokeWidth={2.3} name="Ordered" />
                    <Line type="monotone" dataKey="deliveredValue" stroke="#0d9488" strokeWidth={2.3} name="Delivered" />
                    <Line type="monotone" dataKey="paidValue" stroke="#2563eb" strokeWidth={2.3} name="Paid" />
                    <Line type="monotone" dataKey="issuedValue" stroke="#ea580c" strokeWidth={2.3} name="Issued" />
                    <Line type="monotone" dataKey="remainingBudgetValue" stroke="#dc2626" strokeWidth={2.3} name="Budget Remaining" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <CompareArrowsIcon sx={{ color: '#4b5563' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Permutation Analytics
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#334155' }}>
                    Delivered vs Paid
                  </Typography>
                  <Box sx={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer>
                      <ComposedChart data={analyticsRows}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="bucket" />
                        <YAxis
                          tickMargin={8}
                          width={90}
                          tickFormatter={(value) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)}
                        />
                        <Tooltip formatter={(value: number) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)} />
                        <Legend />
                        <Bar dataKey="deliveredValue" fill="#0d9488" name="Delivered" />
                        <Line type="monotone" dataKey="paidValue" stroke="#2563eb" strokeWidth={2.4} name="Paid" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#334155' }}>
                    Undelivered vs Paid
                  </Typography>
                  <Box sx={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer>
                      <ComposedChart
                        data={analyticsRows.map((row) => ({
                          ...row,
                          undeliveredValue: Math.max(row.orderedValue - row.deliveredValue, 0),
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="bucket" />
                        <YAxis
                          tickMargin={8}
                          width={90}
                          tickFormatter={(value) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)}
                        />
                        <Tooltip formatter={(value: number) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)} />
                        <Legend />
                        <Area type="monotone" dataKey="undeliveredValue" stroke="#f43f5e" fill="#fda4af" name="Undelivered" />
                        <Line type="monotone" dataKey="paidValue" stroke="#2563eb" strokeWidth={2.4} name="Paid" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#334155' }}>
                    Undelivered vs Remaining Budget
                  </Typography>
                  <Box sx={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer>
                      <LineChart
                        data={analyticsRows.map((row) => ({
                          ...row,
                          undeliveredValue: Math.max(row.orderedValue - row.deliveredValue, 0),
                        }))}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="bucket" />
                        <YAxis
                          tickMargin={8}
                          width={90}
                          tickFormatter={(value) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)}
                        />
                        <Tooltip formatter={(value: number) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)} />
                        <Legend />
                        <Line type="monotone" dataKey="undeliveredValue" stroke="#f97316" strokeWidth={2.4} name="Undelivered" />
                        <Line type="monotone" dataKey="remainingBudgetValue" stroke="#dc2626" strokeWidth={2.4} name="Remaining Budget" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#334155' }}>
                    Issued vs Remaining Budget
                  </Typography>
                  <Box sx={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer>
                      <LineChart data={analyticsRows}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="bucket" />
                        <YAxis
                          tickMargin={8}
                          width={90}
                          tickFormatter={(value) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)}
                        />
                        <Tooltip formatter={(value: number) => formatCurrency(value, selectedCurrency === 'All' ? 'USD' : selectedCurrency)} />
                        <Legend />
                        <Line type="monotone" dataKey="issuedValue" stroke="#ea580c" strokeWidth={2.4} name="Issued" />
                        <Line type="monotone" dataKey="remainingBudgetValue" stroke="#1d4ed8" strokeWidth={2.4} name="Remaining Budget" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                Innovative Analytics
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
                Executive risk map: Each bubble shows one time bucket. Bigger bubbles mean bigger procurement activity. High paid-but-low-delivered bubbles indicate pressure points.
              </Typography>

              <Box sx={{ width: '100%', height: 330 }}>
                <ResponsiveContainer>
                  <ScatterChart
                    margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                    data={analyticsRows.map((row) => ({
                      bucket: row.bucket,
                      delivered: row.deliveredValue,
                      paid: row.paidValue,
                      exposure: Math.max(row.paidValue - row.deliveredValue, 0),
                      ordered: row.orderedValue,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="delivered"
                      name="Delivered"
                      tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                    />
                    <YAxis
                      type="number"
                      dataKey="paid"
                      name="Paid"
                      tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                    />
                    <ZAxis type="number" dataKey="ordered" range={[80, 450]} name="Ordered" />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Legend />
                    <Scatter name="Risk Buckets" dataKey="exposure" fill="#ef4444" />
                  </ScatterChart>
                </ResponsiveContainer>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#fef2f2', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#991b1b', fontWeight: 700 }}>
                      Delivery Gap
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#7f1d1d', fontWeight: 800 }}>
                      {formatCurrency(undeliveredValue, 'usd')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#7f1d1d' }}>
                      {undeliveredQty.toLocaleString(undefined, { maximumFractionDigits: 2 })} quantity still undelivered
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#ecfeff', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#155e75', fontWeight: 700 }}>
                      Payment Coverage of Delivered
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#164e63', fontWeight: 800 }}>
                      {deliveredValue > 0 ? `${((paidValue / deliveredValue) * 100).toFixed(1)}%` : '0.0%'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#164e63' }}>
                      Paid value against delivered value
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#f0fdf4', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#166534', fontWeight: 700 }}>
                      Budget Burn
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#14532d', fontWeight: 800 }}>
                      {budgetValue > 0 ? `${((paidValue / budgetValue) * 100).toFixed(1)}%` : '0.0%'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#14532d' }}>
                      Paid against current BOM budget
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper> */}
          </>
        )}
      </Stack>
    </ProcurementContainer>
  );
};

export default CostToCompletionPage;
